import React, { ReactNode, useContext } from 'react';
import { M } from '@dashboard-experience/mastodon';
import T from 'components/T';
import { StatusTypes } from '@dashboard-experience/utils';
import styled from 'styled-components';
import { StatusBadgeWrapperStyled } from 'components/StatusBadge/styles';
import StatusBadge from 'components/StatusBadge/StatusBadge';
import StatusBadgeWithInfo from 'components/StatusBadge/StatusBadgeWithInfo';
import StatusBadgeInfoContext from 'components/StatusBadge/StatusBadgeInfoContext';
import PanelTitleInfo from './PanelTitleInfo';

const CardHeaderWrapperStyled = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export interface PanelProps {
  children: ReactNode;
  className?: string;
  dataTestId?: string;
  i18n?: boolean;
  icon?: string;
  noBlock?: boolean;
  statusType?: string;
  status?: string;
  secondaryStatus?: string | null;
  timestamp?: string;
  title?: string | ReactNode;
  tooltip?: string | ReactNode;
}

const PanelComponent: React.FC<PanelProps> = ({
  children,
  className = '',
  dataTestId,
  i18n = false,
  icon = '',
  noBlock = false,
  statusType = StatusTypes.Legacy,
  status = '',
  secondaryStatus = null,
  timestamp = '',
  title = '',
  tooltip = '',
}) => {
  const showInfo = useContext(StatusBadgeInfoContext);
  const Badge = showInfo ? StatusBadgeWithInfo : StatusBadge;
  return (
    <div
      data-testid={dataTestId}
      className={`no-pagebreak cp-card cp-clearfix ${
        status ? `card-${status}` : ''
      } ${className}`}
    >
      {title && (
        <div className='cp-card-header'>
          <CardHeaderWrapperStyled>
            <div>
              {icon && (
                <>
                  <M.Icon
                    style={{ marginRight: 0 }}
                    icon={icon}
                    data-testid='main-icon'
                  />{' '}
                </>
              )}
              {i18n && typeof title === 'string' ? (
                <T value={title} dangerousHTML />
              ) : (
                title
              )}
              {tooltip && showInfo && <PanelTitleInfo label={tooltip} />}
              {tooltip && !showInfo && (
                <span>
                  <M.Tooltip align='top' label={tooltip}>
                    <M.Icon
                      icon='InformationFilled'
                      data-testid='tooltip-icon'
                    />
                  </M.Tooltip>
                </span>
              )}
              {timestamp && <small className='timestamp'>{timestamp}</small>}
            </div>
            <StatusBadgeWrapperStyled>
              {secondaryStatus && (
                <>
                  <Badge status={secondaryStatus} statusType={statusType} />
                  <span
                    className='cds--header__nav mastodon-header-navigation'
                    style={{ marginInlineStart: '16px', paddingBottom: '8px' }}
                  />
                </>
              )}
              {status && <Badge status={status} statusType={statusType} />}
            </StatusBadgeWrapperStyled>
          </CardHeaderWrapperStyled>
        </div>
      )}
      {noBlock || !children || React.Children.toArray(children).length === 0 ? (
        children
      ) : (
        <div className='cp-card-body' data-testid='card-body'>
          {children}
        </div>
      )}
    </div>
  );
};

export default PanelComponent;
